import { generateShortId, generateUUID } from '../utils/uuid'
import storage from '../utils/storage'
import { getDeviceContext, getPageContext } from '../utils/device'
import queue from './queue'
import type { BaseEvent } from '../types/events'

export class Session {
  private static instance: Session
  private sessionId: string = ''
  private anonymousId: string = ''
  private userId: string | null = null
  private sessionStartTime: number = 0
  private pageViewCount: number = 0
  private interactionCount: number = 0
  private errorCount: number = 0

  private constructor() {}

  public static getInstance(): Session {
    if (!Session.instance) {
      Session.instance = new Session()
    }
    return Session.instance
  }

  /**
   * Initialize session
   */
  public initialize(): void {
    // Get or create anonymous ID (persists across sessions)
    this.anonymousId = this.getOrCreateAnonymousId()

    // Get user ID if exists
    this.userId = storage.get('_radar_snap_user_id')

    // Create new session ID
    this.sessionId = generateShortId('sess_')
    this.sessionStartTime = Date.now()

    // Track session start
    this.trackSessionStart()
  }

  /**
   * Get or create anonymous ID
   */
  private getOrCreateAnonymousId(): string {
    const storageKey = '_radar_snap_anonymous_id'
    let anonymousId = storage.get(storageKey)

    if (!anonymousId) {
      anonymousId = generateShortId('anon_')
      storage.set(storageKey, anonymousId)
    }

    return anonymousId
  }

  /**
   * Track session start event
   */
  private trackSessionStart(): void {
    const pageContext = getPageContext()
    
    this.trackEvent('session_start', {
      entry_page: pageContext.url,
      entry_time: new Date().toISOString(),
      utm_source: pageContext.query_params.utm_source || null,
      utm_medium: pageContext.query_params.utm_medium || null,
      utm_campaign: pageContext.query_params.utm_campaign || null
    })
  }

  /**
   * Track session end event
   */
  public trackSessionEnd(): void {
    const duration = Date.now() - this.sessionStartTime

    this.trackEvent('session_end', {
      exit_page: window.location.href,
      duration: Math.floor(duration / 1000), // seconds
      page_view_count: this.pageViewCount,
      interaction_count: this.interactionCount,
      error_count: this.errorCount,
      status: this.errorCount > 0 ? 'error' : 'success'
    })
  }

  /**
   * Track a generic event
   */
  public trackEvent(eventType: string, eventData: Record<string, any> = {}): void {
    const event: BaseEvent = {
      event_id: generateUUID(),
      session_id: this.sessionId,
      user_id: this.userId || undefined,
      anonymous_id: this.anonymousId,
      timestamp: new Date().toISOString(),
      event_type: eventType,
      event_data: eventData,
      page: getPageContext(),
      device: getDeviceContext()
    }

    queue.enqueue(event)

    // Update counters
    if (eventType === 'pageview') {
      this.pageViewCount++
    } else if (eventType === 'click' || eventType === 'scroll') {
      this.interactionCount++
    } else if (eventType === 'error') {
      this.errorCount++
    }
  }

  /**
   * Set user ID (after login)
   */
  public identify(userId: string): void {
    this.userId = userId
    storage.set('_radar_snap_user_id', userId)

    // Track identify event
    this.trackEvent('identify', { user_id: userId })
  }

  /**
   * Get session info
   */
  public getSessionInfo() {
    return {
      sessionId: this.sessionId,
      anonymousId: this.anonymousId,
      userId: this.userId,
      startTime: this.sessionStartTime,
      pageViewCount: this.pageViewCount,
      interactionCount: this.interactionCount,
      errorCount: this.errorCount
    }
  }

  /**
   * Get session ID
   */
  public getSessionId(): string {
    return this.sessionId
  }

  /**
   * Get anonymous ID
   */
  public getAnonymousId(): string {
    return this.anonymousId
  }

  /**
   * Get user ID
   */
  public getUserId(): string | null {
    return this.userId
  }
}

export default Session.getInstance()